import React, { useState, useEffect, useCallback, useRef } from 'react';
import AppLayout from '../../components/layout/AppLayout';
import {
  getSchedule, getDailyLogs, getDailySummary, toggleTaskLog,
  getCleaningStaff, getAssignments, assignStaff, unassignStaff, autoAssignStaff,
} from '../../api/housekeeping';

const TYPE_CONFIG = {
  reporting: { bg: '#043959', light: 'rgba(4,57,89,0.07)',    label: 'Reporting' },
  work:      { bg: '#10B981', light: 'rgba(16,185,129,0.07)', label: 'Work'      },
  break:     { bg: '#F59E0B', light: 'rgba(245,158,11,0.07)', label: 'Break'     },
};

const AVATAR_COLORS = [
  '#3B82F6','#8B5CF6','#EC4899','#F97316','#14B8A6',
  '#EF4444','#06B6D4','#84CC16','#F59E0B','#6366F1',
];

function avatarColor(id) {
  return AVATAR_COLORS[id % AVATAR_COLORS.length];
}

function initials(name) {
  return name ? name.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2) : '?';
}

function formatTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hour = parseInt(h, 10);
  return `${hour % 12 || 12}:${m} ${hour >= 12 ? 'PM' : 'AM'}`;
}

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

// Staff picker popover for a single task
function StaffPicker({ task, date, allStaff, assigned, onAssign, onUnassign }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const ref = useRef();

  useEffect(() => {
    function handle(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const assignedIds = new Set(assigned.map(a => a.staff_id));
  const available   = allStaff.filter(s => !assignedIds.has(s.id));
  const needed      = task.staff_count - assigned.length;

  const handleAssign = async (staffId) => {
    setBusy(staffId);
    try { await onAssign(task.id, staffId, date); } finally { setBusy(null); }
  };

  const handleUnassign = async (assignmentId) => {
    setBusy(assignmentId);
    try { await onUnassign(assignmentId, task.id); } finally { setBusy(null); }
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        title="Assign staff"
        style={{
          width: 26, height: 26, borderRadius: 6, border: '1.5px dashed #D0D0CC',
          background: open ? '#F0F0EB' : 'white', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          color: '#999', fontSize: 16, fontWeight: 300,
        }}
      >+</button>

      {open && (
        <div
          style={{
            position: 'absolute', zIndex: 100, top: 30, right: 0,
            background: 'white', border: '1px solid #E5E5E0', borderRadius: 12,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: 220,
            padding: '12px 0',
          }}
        >
          {/* Currently assigned */}
          {assigned.length > 0 && (
            <div style={{ padding: '0 12px 8px', borderBottom: '1px solid #F0F0EB', marginBottom: 8 }}>
              <div className="text-xs font-semibold mb-2" style={{ color: '#999', letterSpacing: '0.5px' }}>ASSIGNED</div>
              {assigned.map(a => (
                <div key={a.assignment_id} className="flex items-center gap-2 py-1.5">
                  <div style={{
                    width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                    background: avatarColor(a.staff_id), color: 'white',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 10, fontWeight: 700,
                  }}>
                    {initials(a.staff_name)}
                  </div>
                  <span className="flex-1 text-sm" style={{ color: '#1A1A1A' }}>{a.staff_name}</span>
                  <button
                    onClick={() => handleUnassign(a.assignment_id)}
                    disabled={busy === a.assignment_id}
                    style={{ color: '#EF4444', background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px', fontSize: 16, lineHeight: 1 }}
                  >×</button>
                </div>
              ))}
            </div>
          )}

          {/* Available staff to add */}
          {available.length > 0 ? (
            <div style={{ padding: '0 12px' }}>
              <div className="text-xs font-semibold mb-2" style={{ color: '#999', letterSpacing: '0.5px' }}>
                ADD STAFF {needed > 0 && <span style={{ color: '#F59E0B' }}>({needed} needed)</span>}
              </div>
              <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                {available.map(s => (
                  <button
                    key={s.id}
                    onClick={() => handleAssign(s.id)}
                    disabled={busy === s.id}
                    className="w-full flex items-center gap-2 py-1.5 rounded-lg text-left"
                    style={{ background: 'none', border: 'none', cursor: busy === s.id ? 'wait' : 'pointer', padding: '6px 4px' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#F5F5F5'}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}
                  >
                    <div style={{
                      width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                      background: avatarColor(s.id), color: 'white',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 10, fontWeight: 700,
                    }}>
                      {initials(s.name)}
                    </div>
                    <span className="text-sm" style={{ color: '#1A1A1A' }}>{s.name}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-xs text-center py-2" style={{ color: '#999', padding: '4px 12px' }}>
              {allStaff.length === 0 ? 'No cleaning staff found' : 'All staff assigned'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function HousekeepingPage() {
  const [schedule,    setSchedule]    = useState([]);
  const [logs,        setLogs]        = useState({});
  const [summary,     setSummary]     = useState(null);
  const [assignments, setAssignments] = useState({});  // keyed by task_id → array
  const [allStaff,    setAllStaff]    = useState([]);
  const [date,        setDate]        = useState(todayStr());
  const [loading,     setLoading]     = useState(true);
  const [toggling,    setToggling]    = useState(null);
  const [autoAssigning, setAutoAssigning] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [schRes, logRes, sumRes, asgRes, staffRes] = await Promise.all([
        getSchedule(),
        getDailyLogs(date),
        getDailySummary(date),
        getAssignments(date),
        getCleaningStaff(),
      ]);

      setSchedule(schRes.data || []);

      const logsObj = {};
      Object.values(logRes.data || {}).forEach(l => { logsObj[l.task_id] = l; });
      setLogs(logsObj);

      setSummary(sumRes.data || null);

      // assignments come as object keyed by task_id
      const rawAsg = asgRes.data || {};
      const asgObj = {};
      Object.entries(rawAsg).forEach(([tid, list]) => { asgObj[Number(tid)] = list; });
      setAssignments(asgObj);

      const staff = staffRes.data || [];
      setAllStaff(staff);

      // Auto-assign if no assignments exist for this date
      const hasAny = Object.keys(asgRes.data || {}).length > 0;
      if (!hasAny && staff.length > 0) {
        try {
          const autoRes = await autoAssignStaff(date);
          const rawAuto = autoRes.data || {};
          const autoObj = {};
          Object.entries(rawAuto).forEach(([tid, list]) => { autoObj[Number(tid)] = list; });
          setAssignments(autoObj);
        } catch (_) {}
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleToggle = async (taskId) => {
    setToggling(taskId);
    try {
      const res = await toggleTaskLog(taskId, date);
      const updated = res.data;
      setLogs(prev => ({ ...prev, [taskId]: updated }));
      setSummary(prev => {
        if (!prev) return prev;
        const nowDone = updated.status === 'completed';
        const completed = nowDone ? prev.completed + 1 : prev.completed - 1;
        return { ...prev, completed, pending: prev.total - completed, percentage: prev.total > 0 ? Math.round((completed / prev.total) * 100) : 0 };
      });
    } finally { setToggling(null); }
  };

  const handleAssign = async (taskId, staffId) => {
    const res = await assignStaff(taskId, staffId, date);
    const newEntry = res.data;
    setAssignments(prev => ({
      ...prev,
      [taskId]: [...(prev[taskId] || []), newEntry],
    }));
  };

  const handleAutoAssign = async () => {
    if (!window.confirm(`Auto-assign all ${allStaff.length} cleaning staff across tasks for ${date}? This will replace existing assignments.`)) return;
    setAutoAssigning(true);
    try {
      const res = await autoAssignStaff(date);
      const rawAsg = res.data || {};
      const asgObj = {};
      Object.entries(rawAsg).forEach(([tid, list]) => { asgObj[Number(tid)] = list; });
      setAssignments(asgObj);
    } catch (err) {
      console.error(err);
      alert('Auto-assign failed. Please try again.');
    } finally {
      setAutoAssigning(false);
    }
  };

  const handleUnassign = async (assignmentId, taskId) => {
    await unassignStaff(assignmentId);
    setAssignments(prev => ({
      ...prev,
      [taskId]: (prev[taskId] || []).filter(a => a.assignment_id !== assignmentId),
    }));
  };

  const totalStaff = schedule
    .filter(b => b.block_type === 'work')
    .reduce((s, b) => s + b.tasks.reduce((a, t) => a + t.staff_count, 0), 0);

  return (
    <AppLayout title="Housekeeping Schedule" breadcrumbs="Housekeeping">
      <div style={{ maxWidth: 960, margin: '0 auto' }}>

        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="text-xl font-bold" style={{ color: '#1A1A1A' }}>Daily Work Schedule</h2>
            <p className="text-sm mt-0.5" style={{ color: '#999' }}>
              Assign cleaning staff to tasks and track daily completion
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleAutoAssign}
              disabled={autoAssigning || allStaff.length === 0}
              className="text-sm font-medium rounded-lg text-white transition-all hover:opacity-90 flex items-center gap-2"
              style={{ padding: '8px 16px', background: '#043959', opacity: autoAssigning ? 0.7 : 1, cursor: autoAssigning ? 'wait' : 'pointer', border: 'none', fontFamily: "'Lexend', sans-serif" }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
              {autoAssigning ? 'Assigning...' : `Auto-Assign (${allStaff.length} staff)`}
            </button>
            <input
              type="date" value={date}
              onChange={e => setDate(e.target.value)}
              className="text-sm rounded-lg"
              style={{ padding: '8px 12px', border: '1px solid #E5E5E0', fontFamily: "'Lexend', sans-serif", outline: 'none' }}
            />
          </div>
        </div>

        {/* Summary cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
            {[
              { label: 'Total Tasks',       value: summary.total,               color: '#043959' },
              { label: 'Completed',         value: summary.completed,            color: '#10B981' },
              { label: 'Pending',           value: summary.pending,              color: '#F59E0B' },
              { label: 'Cleaning Staff',    value: allStaff.length,              color: '#8B5CF6' },
            ].map(c => (
              <div key={c.label} className="bg-white rounded-2xl text-center" style={{ border: '1px solid #E5E5E0', padding: '16px 12px' }}>
                <div className="text-2xl font-bold mb-0.5" style={{ color: c.color }}>{c.value}</div>
                <div className="text-xs" style={{ color: '#999' }}>{c.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Progress bar */}
        {summary && (
          <div className="bg-white rounded-2xl mb-5" style={{ border: '1px solid #E5E5E0', padding: '14px 20px' }}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium" style={{ color: '#1A1A1A' }}>Daily Completion</span>
              <span className="text-sm font-semibold" style={{ color: '#10B981' }}>{summary.completed}/{summary.total} done · {summary.percentage}%</span>
            </div>
            <div style={{ height: 7, background: '#F0F0EB', borderRadius: 99 }}>
              <div style={{
                height: '100%', borderRadius: 99,
                background: summary.percentage === 100 ? '#10B981' : '#3B82F6',
                width: `${summary.percentage}%`, transition: 'width 0.4s ease',
              }} />
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-16 text-sm" style={{ color: '#999' }}>Loading schedule...</div>
        ) : (
          <div className="flex flex-col gap-4">
            {schedule.map((block) => {
              const cfg    = TYPE_CONFIG[block.block_type] || TYPE_CONFIG.work;
              const isRest = block.block_type !== 'work';
              const blockDone = block.tasks.filter(t => logs[t.id]?.status === 'completed').length;

              return (
                <div key={block.id} className="bg-white rounded-2xl"
                  style={{ border: '1px solid #E5E5E0', borderLeft: `4px solid ${cfg.bg}` }}>

                  {/* Block header */}
                  <div className="flex flex-wrap items-center justify-between gap-2"
                    style={{ padding: '13px 20px', background: cfg.light, borderBottom: block.tasks.length > 0 ? '1px solid #E5E5E0' : 'none', borderRadius: '16px 16px 0 0' }}>
                    <div className="flex items-center gap-3">
                      <div className="text-xs font-semibold whitespace-nowrap" style={{ color: cfg.bg, minWidth: 100 }}>
                        {formatTime(block.start_time)}{block.end_time && ` – ${formatTime(block.end_time)}`}
                      </div>
                      <div>
                        <div className="font-semibold text-sm" style={{ color: '#1A1A1A' }}>{block.title}</div>
                        {!isRest && block.tasks.length > 0 && (
                          <div className="text-xs mt-0.5" style={{ color: '#999' }}>
                            {block.tasks.reduce((s, t) => s + t.staff_count, 0)} staff required · {block.tasks.length} area{block.tasks.length > 1 ? 's' : ''}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {!isRest && block.tasks.length > 0 && (
                        <span className="text-xs font-semibold px-2 py-1 rounded-lg"
                          style={{
                            background: blockDone === block.tasks.length ? 'rgba(16,185,129,0.15)' : 'rgba(0,0,0,0.06)',
                            color: blockDone === block.tasks.length ? '#10B981' : '#666',
                          }}>
                          {blockDone}/{block.tasks.length} done
                        </span>
                      )}
                      <span className="text-xs font-semibold px-2 py-1 rounded-full"
                        style={{ background: cfg.bg, color: 'white' }}>{cfg.label}</span>
                    </div>
                  </div>

                  {/* Task rows */}
                  {block.tasks.length > 0 && (
                    <div style={{ padding: '10px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {block.tasks.map((task) => {
                        const log      = logs[task.id];
                        const done     = log?.status === 'completed';
                        const assigned = assignments[task.id] || [];
                        const needed   = task.staff_count - assigned.length;
                        const isBusy   = toggling === task.id;

                        return (
                          <div key={task.id} className="rounded-xl transition-all"
                            style={{
                              padding: '10px 14px',
                              background: done ? 'rgba(16,185,129,0.05)' : '#FAFAF8',
                              border: `1px solid ${done ? 'rgba(16,185,129,0.2)' : '#F0F0EB'}`,
                              display: 'flex', alignItems: 'flex-start', gap: 12,
                            }}>

                            {/* Done toggle */}
                            <button
                              onClick={() => handleToggle(task.id)}
                              disabled={isBusy}
                              style={{
                                width: 22, height: 22, borderRadius: 5, flexShrink: 0, marginTop: 1,
                                border: done ? 'none' : '2px solid #D0D0CC',
                                background: done ? '#10B981' : 'white',
                                cursor: isBusy ? 'wait' : 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                transition: 'all 0.2s',
                              }}
                            >
                              {done && <svg width="11" height="8" viewBox="0 0 11 8" fill="none">
                                <path d="M1 4L4 7L10 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>}
                            </button>

                            {/* Area name */}
                            <span className="flex-1 text-sm" style={{
                              color: done ? '#10B981' : '#1A1A1A',
                              textDecoration: done ? 'line-through' : 'none',
                              textDecorationColor: '#10B981',
                              paddingTop: 2,
                            }}>
                              {task.area}
                            </span>

                            {/* Right: all in one row */}
                            <div className="flex items-center gap-2 flex-wrap" style={{ flexShrink: 0, justifyContent: 'flex-end' }}>
                              {/* Staff required badge */}
                              <div className="flex items-center gap-1 text-xs font-semibold"
                                style={{ padding: '3px 9px', borderRadius: 12, background: 'rgba(4,57,89,0.08)', color: '#043959', whiteSpace: 'nowrap' }}>
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                                </svg>
                                {task.staff_count} req
                              </div>

                              {/* Assigned staff names inline */}
                              {assigned.map(a => (
                                <div key={a.assignment_id} className="flex items-center gap-1" style={{ fontSize: 12 }}>
                                  <div style={{ width: 7, height: 7, borderRadius: '50%', background: avatarColor(a.staff_id), flexShrink: 0 }}/>
                                  <span style={{ color: '#444', fontWeight: 500, whiteSpace: 'nowrap' }}>{a.staff_name}</span>
                                  <button
                                    onClick={() => handleUnassign(a.assignment_id, task.id)}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#CCC', padding: 0, lineHeight: 1, fontSize: 13 }}
                                    title="Remove"
                                  >×</button>
                                </div>
                              ))}

                              {needed > 0 && <span style={{ fontSize: 11, color: '#F59E0B', fontWeight: 500, whiteSpace: 'nowrap' }}>{needed} more</span>}
                              {needed <= 0 && assigned.length > 0 && <span style={{ fontSize: 11, color: '#10B981', fontWeight: 500 }}>✓ Full</span>}

                              <StaffPicker task={task} date={date} allStaff={allStaff}
                                assigned={assigned} onAssign={handleAssign} onUnassign={handleUnassign} />
                            </div>

                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="text-center text-xs mt-8 pb-4" style={{ color: '#CCC' }}>
          {totalStaff} total staff positions across all tasks
        </div>
      </div>
    </AppLayout>
  );
}
